import React, { useState, useEffect } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  IconButton,
  Collapse,
  Typography,
  Grid
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useDebounce } from '@hooks/useDebounce';

interface FilterField {
  name: string;
  label: string;
  type?: string;
}

interface FilterValues {
  [key: string]: string;
}

interface DataFilterProps {
  fields: FilterField[];
  onFilter: (filters: FilterValues) => void;
  autoSubmit?: boolean;
  debounceTime?: number;
}

const DataFilter = ({ 
  fields, 
  onFilter, 
  autoSubmit = true,
  debounceTime = 500 
}: DataFilterProps) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const debouncedFilters = useDebounce(filterValues, debounceTime);

  useEffect(() => {
    if (autoSubmit) {
      const activeFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([_, value]) => value !== '')
      );
      onFilter(activeFilters);
    }
  }, [debouncedFilters, autoSubmit, onFilter]);

  const handleFilterChange = (field: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    if (!autoSubmit) {
      const activeFilters = Object.fromEntries(
        Object.entries(filterValues).filter(([_, value]) => value !== '')
      );
      onFilter(activeFilters);
    }
  };

  const handleClearFilters = () => {
    setFilterValues({});
    onFilter({});
  };

  return (
    <Paper sx={{ mb: 2, p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>
            Filtros
          </Typography>
        </Box>
        <Box>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{ mr: 1 }}
          >
            Limpar
          </Button>
          {!autoSubmit && (
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplyFilter}
            >
              Aplicar
            </Button>
          )}
        </Box>
      </Box>

      <Collapse in={showFilters}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {fields.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                type={field.type || 'text'}
                value={filterValues[field.name] || ''}
                onChange={(e) => handleFilterChange(field.name, e.target.value)}
                size="small"
                placeholder={`Buscar por ${field.label.toLowerCase()}`}
              />
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default DataFilter; 